// ════════════════════════════════════════════════════════════════════
// UI PRIMITIVES — shared presentational components (ConfirmDialog + Btn,
// Inp, Card, StatCard, EmptyState, SectionHdr, ChartTip). Extracted
// verbatim from App.js. ChartTip uses the currency-aware fmt() helper.
// ════════════════════════════════════════════════════════════════════
import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { C } from "../lib/theme";
import { fmt } from "../lib/format";

// Click-to-toggle info tooltip (ⓘ). The popup renders in a PORTAL to
// document.body with position:fixed, so it can't be clipped by a card's
// overflow (StatCard) or trapped behind a sibling's stacking context (the
// `.card-enter` fadeInUp animation retains transform:translateY(0), which
// creates a per-card stacking context). Closes on outside-click / scroll /
// resize. KEEPS a title= attribute as a hover fallback.
export function InfoTip({ text, align = "left" }) {
  const [open, setOpen] = useState(false);
  const [hov, setHov] = useState(false);
  const [pos, setPos] = useState(null);
  const iconRef = useRef(null);
  const popRef = useRef(null);
  // useLayoutEffect + pos-reset-on-close place the popup BEFORE paint, so
  // reopening (after a scroll moved the icon) never flashes at stale coords.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const el = iconRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const w = Math.min(260, window.innerWidth - 16);          // shrink to fit narrow viewports
      let left = align === "right" ? r.right - w : r.left;
      left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
      const caret = Math.max(9, Math.min(r.left + r.width / 2 - left - 4.5, w - 12)); // track the ⓘ
      // Flip ABOVE (anchored by `bottom`, so no height measurement needed) when
      // there isn't room below but there is above — avoids viewport-bottom clip.
      const flip = r.bottom + 138 > window.innerHeight && r.top > 142;
      setPos(flip
        ? { w, left, caret, flip: true, bottom: window.innerHeight - r.top + 7 }
        : { w, left, caret, flip: false, top: r.bottom + 7 });
    };
    place();
    const close = () => setOpen(false);
    const onDown = (e) => {
      if (iconRef.current && iconRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      setPos(null);
    };
  }, [open, align]);
  const active = open || hov;
  return (
    <span ref={iconRef} style={{ position: "relative", display: "inline-flex", verticalAlign: "middle" }}>
      <span role="img" aria-label={text} title={text}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, borderRadius: "50%", border: `1.4px solid ${active ? C.blue : C.muted}`, background: active ? "rgba(44,80,221,.08)" : "transparent", color: active ? C.blue : C.sub, fontSize: 11, fontWeight: 700, cursor: "pointer", lineHeight: 1, userSelect: "none", transition: "all .12s ease" }}>
        i
      </span>
      {open && pos && createPortal(
        <span ref={popRef} style={{ position: "fixed", left: pos.left, width: pos.w, ...(pos.flip ? { bottom: pos.bottom } : { top: pos.top }), zIndex: 4000, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: C.s1, padding: 11, fontSize: 11.5, lineHeight: 1.5, color: C.sub, fontWeight: 500, fontStyle: "normal", textAlign: "left", whiteSpace: "normal", letterSpacing: 0, textTransform: "none" }}>
          <span style={{ position: "absolute", left: pos.caret, ...(pos.flip ? { bottom: -5 } : { top: -5 }), width: 9, height: 9, background: "#fff", ...(pos.flip ? { borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` } : { borderLeft: `1px solid ${C.border}`, borderTop: `1px solid ${C.border}` }), transform: "rotate(45deg)" }}/>
          {text}
        </span>,
        document.body
      )}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CONFIRM DIALOG — replaces window.confirm() with styled modal
// ═══════════════════════════════════════════════════════════════════════
// Usage:
//   const [confirm, setConfirm] = useState(null);
//   setConfirm({
//     title: "Cancel workspace?",
//     body: "...",
//     confirmLabel: "Cancel workspace",
//     confirmVariant: "danger",  // 'danger' | 'primary'
//     onConfirm: async () => { ... },
//   });
//   {confirm && <ConfirmDialog {...confirm} onClose={()=>setConfirm(null)}/>}
export function ConfirmDialog({ title, body, confirmLabel = "Confirm", cancelLabel = "Cancel", confirmVariant = "primary", onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  async function handleConfirm() {
    if (busy) return;
    setBusy(true);
    setErrorMsg("");
    try {
      await onConfirm?.();
      onClose?.();
    } catch (e) {
      console.error("ConfirmDialog error:", e);
      setErrorMsg(e?.message || "Something went wrong. Please try again.");
      setBusy(false);
    }
  }

  // ESC to cancel
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape" && !busy) onClose?.();
      if (e.key === "Enter" && !busy) handleConfirm();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);

  const isDanger = confirmVariant === "danger";

  return (
    <div onClick={busy ? undefined : onClose} style={{position:"fixed",inset:0,background:"rgba(15,23,42,.5)",backdropFilter:"blur(4px)",WebkitBackdropFilter:"blur(4px)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:"'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",animation:"fadeIn .15s ease-out"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:14,maxWidth:440,width:"100%",boxShadow:"0 24px 60px rgba(15,23,42,.25)",border:"1px solid #e2e8f0",overflow:"hidden",animation:"scaleIn .15s ease-out"}}>
        <div style={{padding:"24px 26px 18px"}}>
          {isDanger && (
            <div style={{width:40,height:40,borderRadius:10,background:"#fef2f2",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:14}}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 9v4M12 17h.01"/>
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              </svg>
            </div>
          )}
          <h3 style={{fontSize:17,fontWeight:700,color:"#0f172a",letterSpacing:"-.3px",margin:"0 0 6px",lineHeight:1.3}}>{title}</h3>
          <p style={{fontSize:13.5,color:"#475569",lineHeight:1.55,margin:0,fontWeight:500}}>{body}</p>
          {errorMsg && (
            <div style={{marginTop:14,padding:"10px 12px",background:"#fef2f2",border:"1px solid #fecaca",borderRadius:8,fontSize:12.5,color:"#991b1b",fontWeight:500,lineHeight:1.5}}>
              {errorMsg}
            </div>
          )}
        </div>
        <div style={{padding:"14px 26px 22px",display:"flex",gap:10,justifyContent:"flex-end"}}>
          <button onClick={busy ? undefined : onClose} disabled={busy} style={{padding:"9px 16px",borderRadius:8,border:"1px solid #e2e8f0",background:"#fff",color:"#475569",fontSize:13,fontWeight:600,cursor:busy?"not-allowed":"pointer",fontFamily:"inherit",letterSpacing:"-.1px",opacity:busy?0.5:1,transition:"all .12s"}}>
            {errorMsg ? "Close" : cancelLabel}
          </button>
          {!errorMsg && (
            <button onClick={handleConfirm} disabled={busy} style={{padding:"9px 16px",borderRadius:8,border:"none",background:busy?"#94a3b8":(isDanger?"#dc2626":"#1e1b4b"),color:"#fff",fontSize:13,fontWeight:600,cursor:busy?"wait":"pointer",fontFamily:"inherit",letterSpacing:"-.1px",boxShadow:busy?"none":(isDanger?"0 2px 6px rgba(220,38,38,.25)":"0 2px 6px rgba(15,23,42,.18)"),transition:"all .12s"}}>
              {busy ? "Working…" : confirmLabel}
            </button>
          )}
        </div>
      </div>
      <style>{`
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes scaleIn{from{opacity:0;transform:scale(.95)}to{opacity:1;transform:scale(1)}}
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// PRIMITIVES
// ═══════════════════════════════════════════════════════════════════════
export function Btn({children, onClick, v="primary", size="md", full, sx={}, disabled}) {
  const base = {borderRadius:8,fontWeight:600,cursor:disabled?"not-allowed":"pointer",fontFamily:"inherit",border:"none",transition:"all .15s cubic-bezier(0.4, 0, 0.2, 1)",letterSpacing:"-.1px",opacity:disabled?0.6:1};
  const vars = {
    primary:   {background:`linear-gradient(180deg,${C.blue} 0%,${C.blueDark} 100%)`, color:"#fff", boxShadow:C.sBlue},
    secondary: {background:"#fff",  color:C.blue, border:`1px solid ${C.blue}44`, boxShadow:C.s1},
    ghost:     {background:"transparent", color:C.sub, border:`1px solid ${C.border}`},
    danger:    {background:C.red10, color:C.redDark, border:`1px solid ${C.red}33`},
  };
  const sizes = {sm:"6px 14px", md:"10px 22px", lg:"14px 30px"};
  return (
    <button onClick={onClick} disabled={disabled} style={{...base,...vars[v],padding:sizes[size],fontSize:size==="sm"?12:size==="lg"?15:13.5,width:full?"100%":"auto",...sx}}>
      {children}
    </button>
  );
}

export function Inp({label, value, onChange, type="text", prefix, suffix, hint, placeholder, small}) {
  const fs = small ? 12.5 : 13.5;
  return (
    <div style={{display:"flex",flexDirection:"column",gap:5}}>
      {label && <label style={{fontSize:10.5,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7}}>{label}</label>}
      <div style={{display:"flex",alignItems:"center",background:"#fff",border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden",transition:"all .15s"}}>
        {prefix && <span style={{padding:"0 12px",color:C.muted,fontSize:fs,background:C.bg3,borderRight:`1px solid ${C.border2}`,whiteSpace:"nowrap",alignSelf:"stretch",display:"flex",alignItems:"center",fontWeight:500}}>{prefix}</span>}
        <input type={type} value={value} placeholder={placeholder}
          onChange={e => onChange(type==="number" ? parseFloat(e.target.value)||0 : e.target.value)}
          style={{background:"transparent",border:"none",outline:"none",color:C.text,padding:`${small?8:10}px 12px`,width:"100%",fontSize:fs,fontFamily:"inherit",fontWeight:500}} />
        {suffix && <span style={{padding:"0 12px",color:C.muted,fontSize:fs,background:C.bg3,borderLeft:`1px solid ${C.border2}`,fontWeight:500}}>{suffix}</span>}
      </div>
      {hint && <span style={{fontSize:11,color:C.muted2,lineHeight:1.4}}>{hint}</span>}
    </div>
  );
}

export function Card({children, sx={}, hover}) {
  return <div className="card-enter" style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:12,padding:20,boxShadow:C.s1,transition:"all .2s cubic-bezier(0.4, 0, 0.2, 1)",...sx}}>{children}</div>;
}

export function StatCard({label, value, sub, accent, color, tip}) {
  return (
    <div className="card-enter" style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:12,padding:"16px 18px",boxShadow:C.s1,position:"relative",overflow:tip?"visible":"hidden",transition:"all .2s cubic-bezier(0.4, 0, 0.2, 1)"}}>
      {/* Subtle accent bar */}
      <div style={{position:"absolute",top:0,left:0,right:0,height:3,borderRadius:"12px 12px 0 0",background:`linear-gradient(90deg,${accent||C.border} 0%,${accent||C.border}66 100%)`}}/>
      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8,marginTop:4}}>
        <span style={{fontSize:10.5,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:.7}}>{label}</span>
        {tip||null}
      </div>
      <div className="num" style={{fontSize:24,fontWeight:700,color:color||C.text,letterSpacing:"-.6px",lineHeight:1.1}}>{value}</div>
      {sub && <div style={{fontSize:11,color:C.muted2,marginTop:6,fontWeight:500}}>{sub}</div>}
    </div>
  );
}

// Reusable, professional empty/zero-data state. icon: optional SVG path d string.
export function EmptyState({title, message, action, icon="bars"}) {
  const icons = {
    bars:  <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>,
    rows:  <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>,
    tag:   <><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
  };
  return (
    <div style={{textAlign:"center",padding:"56px 24px",maxWidth:440,margin:"0 auto"}}>
      <div style={{width:56,height:56,borderRadius:14,background:C.blue5,border:`1px solid ${C.blue10}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 18px"}}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{icons[icon]||icons.bars}</svg>
      </div>
      <div style={{fontSize:16,fontWeight:700,color:C.text,marginBottom:7,letterSpacing:"-.2px"}}>{title}</div>
      {message && <div style={{fontSize:13,color:C.muted,lineHeight:1.6}}>{message}</div>}
      {action && <div style={{marginTop:18}}>{action}</div>}
    </div>
  );
}

export function SectionHdr({title, sub, right}) {
  return (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:24,paddingBottom:18,borderBottom:`1px solid ${C.border2}`}}>
      <div>
        <div style={{fontSize:22,fontWeight:700,color:C.text,letterSpacing:"-.6px",lineHeight:1.2}}>{title}</div>
        {sub && <div style={{fontSize:13,color:C.muted,marginTop:6,fontWeight:500}}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

export function ChartTip({active, payload, label}) {
  if (!active||!payload?.length) return null;
  return (
    <div style={{background:"rgba(255,255,255,.97)",backdropFilter:"blur(8px)",border:`1px solid ${C.border}`,borderRadius:10,padding:"11px 14px",boxShadow:C.s3,fontSize:12.5}}>
      <div style={{fontWeight:700,color:C.text,marginBottom:7,fontSize:12,letterSpacing:"-.1px"}}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{display:"flex",justifyContent:"space-between",gap:20,marginBottom:3,alignItems:"center"}}>
          <span style={{color:C.muted,fontSize:11.5,fontWeight:500}}>{p.name}</span>
          <span className="num" style={{fontWeight:700,color:p.color||C.text,fontSize:12}}>{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
}
